import React, { useContext, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PayoutProfileDrawer } from './PayoutProfileDrawer';
import { EditPayoutProfileDrawerContext } from './EditPayoutProfileDrawerContext';
import PropTypes from 'prop-types';
import { z } from 'zod';
import { Frequency, Modes } from './constants';
import { ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../store';
import { PayoutMethods } from '../constants';
import { getPayoutMethod } from '../utils';
import { required } from '../../../../../../utils/validation/zodValidators';
import { SCALE_CURRENCY } from '../../../../../../utils/currencies';

const useFormModel = () => {
    const { t } = useTranslation();

    return useMemo(() => {
        const Base = z.object({
            payoutMethod: z.enum(Object.values(PayoutMethods)),
            bankAccount: required(),
            documentHeader: required(),
            reserve: z.number()
        });

        const WithFrequency = Base.extend({
            payoutMethod: z.literal(PayoutMethods.FREQUENCY),
            frequency: z.enum(Object.values(Frequency), {
                message: t('common.requiredField')
            })
        });

        const WithPayoutThreshold = Base.extend({
            payoutMethod: z.literal(PayoutMethods.PAYOUT_THRESHOLD),
            payoutThreshold: z
                .number({
                    message: t('common.requiredField')
                })
                .gt(0)
        });

        return z.discriminatedUnion('payoutMethod', [WithFrequency, WithPayoutThreshold]);
    }, [t]);
};

const getModeById = id => (id ? Modes.EDIT : Modes.CREATE);

const fieldsToBackend = fields => ({
    bank_account_id: fields.bankAccount,
    document_head_id: fields.documentHeader,
    payout_threshold: fields.payoutMethod === PayoutMethods.PAYOUT_THRESHOLD ? fields.payoutThreshold * SCALE_CURRENCY : 0,
    keep_in_reserve: fields.reserve * SCALE_CURRENCY,
    payout_frequency: fields.frequency
});

const fieldsFromBackend = data => ({
    bankAccount: data.bank_account_id || '',
    documentHeader: data.document_head_id || '',
    payoutThreshold: (data.payout_threshold || 0) / SCALE_CURRENCY,
    reserve: (data.keep_in_reserve || 0) / SCALE_CURRENCY,
    frequency: data.payout_frequency,
    name: data.name || '',
    payoutMethod: getPayoutMethod(data)
});

const EMPTY_STATE = {
    bankAccount: null,
    documentHeader: null,
    payoutThreshold: 0,
    reserve: 0,
    frequency: null,
    name: '',
    payoutMethod: PayoutMethods.FREQUENCY
};

export const EditPayoutProfileDrawerProvider = ({ children }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const mode = getModeById(editId);
    const { payoutProfiles: store } = useStore();
    const toaster = useContext(ToastContext);

    const closeModal = () => setIsOpen(false);

    const createMutation = useMutation(data => store.create(data), {
        onSuccess: () => {
            closeModal();
            toaster.success(t('sauron.financeModule.payoutProfiles.drawer.create.successToast'));
        }
    });

    const updateMutation = useMutation(data => store.update(editId, data), {
        onSuccess: () => {
            closeModal();
            toaster.success(t('sauron.financeModule.payoutProfiles.drawer.edit.successToast'));
        }
    });

    const FormModel = useFormModel();

    const form = useForm({
        resolver: zodResolver(FormModel)
    });

    const { reset } = form;

    const handleSubmit = formData => {
        switch (mode) {
            case Modes.CREATE:
                createMutation.mutate(fieldsToBackend(formData));
                break;
            case Modes.EDIT:
                updateMutation.mutate(fieldsToBackend(formData));
                break;
            default:
                break;
        }
    };

    const value = useMemo(() => {
        const edit = document => {
            setIsOpen(true);
            setEditId(document.id);
            reset(fieldsFromBackend(document));
        };

        const create = () => {
            setIsOpen(true);
            setEditId(null);
            reset(EMPTY_STATE);
        };

        return {
            edit,
            create
        };
    }, [reset]);

    return (
        <EditPayoutProfileDrawerContext.Provider value={value}>
            <FormProvider {...form}>
                <PayoutProfileDrawer
                    isOpen={isOpen}
                    mode={mode}
                    onClose={closeModal}
                    onSubmit={form.handleSubmit(handleSubmit)}
                    isSubmitting={updateMutation.isLoading || createMutation.isLoading}
                />
            </FormProvider>
            {children}
        </EditPayoutProfileDrawerContext.Provider>
    );
};

EditPayoutProfileDrawerProvider.propTypes = {
    children: PropTypes.node
};
